/**
 * ============================================================================
 * Tenant Context - React Context per Multi-Tenant
 * ============================================================================
 *
 * Gestisce la configurazione del tenant corrente in tutta l'applicazione.
 *
 * Uso:
 * ```tsx
 * import { useTenant } from './core/contexts/TenantContext';
 *
 * function MyComponent() {
 *   const { config, isLoading, error } = useTenant();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *
 *   return (
 *     <div>
 *       <h1>{config.app_name}</h1>
 *       {config.apps.lavorazione?.enabled && <LavorazioneApp />}
 *     </div>
 *   );
 * }
 * ```
 */

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Interfacce TypeScript
 */
export interface TenantBranding {
  logo: string;
  primary_color: string;
  accent_color: string;
  company_name?: string;
}

export interface TenantApp {
  enabled: boolean;
  entity_types?: string[];
  photo_types?: string[];
}

export interface TenantApps {
  lavorazione?: TenantApp;
  documenti?: TenantApp;
  bigliettini?: TenantApp;
  ispezioni?: TenantApp;
}

export interface TenantFeatures {
  chat_enabled: boolean;
  ocr_enabled: boolean;
  ai_processing: boolean;
  push_notifications: boolean;
  mobile_photo_requests: boolean;
}

export interface TenantLimits {
  max_users: number;
  max_storage_gb: number;
  max_photos_per_month: number;
}

export interface TenantConfig {
  tenant_id: string;
  subdomain: string;
  app_name: string;
  status: 'active' | 'trial' | 'suspended' | 'expired';
  branding: TenantBranding;
  apps: TenantApps;
  features: TenantFeatures;
  limits: TenantLimits;
}

export interface TenantContextValue {
  config: TenantConfig | null;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Context
 */
const TenantContext = createContext<TenantContextValue | undefined>(undefined);

/**
 * Provider Props
 */
interface TenantProviderProps {
  children: ReactNode;
}

/**
 * Estrae subdomain da hostname
 */
function extractSubdomain(hostname: string): string {
  // Caso localhost / IP → subdomain default
  if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return 'coronatek';
  }

  // Caso dev.coronacrm.it → coronatek
  if (hostname.includes('coronacrm.it')) {
    return 'coronatek';
  }

  // Estrai subdomain da hostname
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return parts[0];
  }

  return 'coronatek'; // Default fallback
}

/**
 * Provider Component
 */
export function TenantProvider({ children }: TenantProviderProps) {
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load tenant config
   */
  const loadConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Estrai subdomain
      const subdomain = extractSubdomain(window.location.hostname);
      console.log(`[TenantContext] Loading config for subdomain: ${subdomain}`);

      // Chiama API
      const response = await fetch(`/api/config/${subdomain}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Errore caricamento configurazione');
      }

      console.log('[TenantContext] Config loaded:', data);

      setConfig({
        tenant_id: data.tenant_id,
        subdomain: data.subdomain,
        app_name: data.app_name,
        status: data.status,
        branding: data.branding,
        apps: data.apps,
        features: data.features,
        limits: data.limits
      });

      setIsLoading(false);
    } catch (err) {
      console.error('[TenantContext] Errore caricamento config:', err);
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
      setIsLoading(false);

      // Fallback a config di default per development
      if (process.env.NODE_ENV === 'development') {
        console.warn('[TenantContext] Usando config di fallback per development');
        setConfig({
          tenant_id: 'coronatek-default',
          subdomain: 'coronatek',
          app_name: 'CoronaTek Manager',
          status: 'active',
          branding: {
            logo: '/assets/logos/coronatek.png',
            primary_color: '#667eea',
            accent_color: '#764ba2',
            company_name: 'CoronaTek S.r.l.'
          },
          apps: {
            lavorazione: {
              enabled: true,
              entity_types: ['turbine', 'fap', 'iniettore']
            },
            documenti: {
              enabled: true,
              photo_types: ['invoice', 'delivery_note', 'generic_document']
            },
            bigliettini: {
              enabled: false
            },
            ispezioni: {
              enabled: false
            }
          },
          features: {
            chat_enabled: true,
            ocr_enabled: true,
            ai_processing: true,
            push_notifications: true,
            mobile_photo_requests: true
          },
          limits: {
            max_users: 100,
            max_storage_gb: 500,
            max_photos_per_month: 50000
          }
        });
        setError(null);
      }
    }
  };

  /**
   * Load config on mount
   */
  useEffect(() => {
    loadConfig();
  }, []);

  /**
   * Context value
   */
  const value: TenantContextValue = {
    config,
    isLoading,
    error,
    reload: loadConfig
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook per accedere al tenant context
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);

  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
}

/**
 * Hook per verificare se un'app è abilitata
 */
export function useAppEnabled(appId: keyof TenantApps): boolean {
  const { config } = useTenant();

  if (!config || !config.apps) return false;

  return config.apps[appId]?.enabled ?? false;
}

/**
 * Hook per verificare se una feature è abilitata
 */
export function useFeatureEnabled(featureId: keyof TenantFeatures): boolean {
  const { config } = useTenant();

  if (!config || !config.features) return false;

  return config.features[featureId] ?? false;
}

/**
 * Export default
 */
export default TenantContext;
